import { getOpxHostApi } from "owa-opx";
import { markFunction } from "owa-performance";
import { hasQueryStringParameter } from "owa-querystring";
import getUserConfiguration from "owa-session-store/lib/actions/getUserConfiguration";
import { loadInitialTheme, getIsSystemDarkModePreferred } from "owa-theme";
import { initializeIsMsHighContrastState, setIsTeamsHighContrast } from "owa-high-contrast";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { isBootFeatureEnabled } from "owa-metatags";
import { isHostAppFeatureEnabled } from "owa-hostapp-feature-flags";
import { logStartUsage } from "owa-analytics-start";
import { lazyHandleThemeChange, lazyRegisterOnLoadHandler, lazyGetCachedContext, lazyGetContext, type OnTabLoadHandlerPriority, lazyGetConfigSetting } from "owa-metaos-app-bootstrap";
import { lazyGetTeamsPalette, lazyGetTeamsBrandVariants } from "owa-teams-palette";
import type { OwaPalette } from "owa-theme-shared";
import { ThemeConstants } from "owa-theme-shared";
import { getGlobalSettingsAccountMailboxInfo, tryGetGlobalSettingsAccountMailboxInfo, } from "owa-account-source-list-store";
import { getAccountScopeUserSettings } from "owa-session-store";
import { generateTeamsThemeId } from "owa-teams-theme-utils";
import type { CopilotThemeOptions } from "owa-copilot-themes-types";
import type { BrandVariants } from "@fluentui/react-components";
import { isCapabilitySupported } from "owa-capabilities";
import { copilotThemeCapability } from "owa-capabilities-definitions/lib/copilotThemeCapability";
import { getOwaWorkload, type OwaWorkload } from "owa-workloads";

let cachedThemeStorageId: string | undefined = undefined;
const defaultExport: () => Promise<void> = markFunction(async function initializeTheme() {
    const userConfig = getUserConfiguration();
    const userOptions = userConfig?.UserOptions;
    // If the cachedThemeStorageId is not present, initializeTheme is running for the first time
    const isInitializeThemeRunningFirstTime = !cachedThemeStorageId;
    let isDarkTheme = !!userOptions?.IsDarkModeTheme;
    let themeStorageId = userOptions?.ThemeStorageId;
    let themesAllowed = !!userConfig?.SegmentationSettings?.Themes;
    let palette: OwaPalette | undefined = undefined;
    let useSystemDarkModeSettings: boolean = false;
    let brandVariants: BrandVariants | undefined = undefined;
    
    if (isHostAppFeatureEnabled("loadThemeFromHostApp")) {
        const opxHostApi = getOpxHostApi();
        isDarkTheme = (await opxHostApi.isDarkTheme()) || false;
        themeStorageId = "base";
        themesAllowed = true;
        palette = hasQueryStringParameter("useOwaTheme")
            ? undefined
            : await opxHostApi.getOpxPalette(isDarkTheme);
        
        if (isInitializeThemeRunningFirstTime) {
            opxHostApi.registerThemeChangedHandler(initializeTheme);
        }
    }
    else if (isHostAppFeatureEnabled("platformAppSdk")) {
        // Get the fresh metaOS context when app is resumed from the cached state, otherwise use the cached context.
        const teamsTheme = await (isInitializeThemeRunningFirstTime ? lazyGetCachedContext : lazyGetContext)
            .importAndExecute()
            .then(context => {
            return context.app.theme;
        });
        // keep in sync with isDarkTheme in owa-metaos-app-bootstrap
        isDarkTheme = teamsTheme === "dark";
        const workload = getOwaWorkload();
        if (isHostAppFeatureEnabled("useTeamsAppearance") &&
            !isGlobalFeatureEnabled("platform-disableHostAppSpecificCustomizations") &&
            workload !== 128 // Excluding the Bookings MetaOS app from utilizing the Teams theme palette.
        ) {
            // When hosted in Teams, the host controls the theme appearance
            // (light/dark/contrast) so we must allow it regardless of the tenant's
            // SegmentationSettings.Themes policy, which only governs user-chosen
            // accent-color personalisation in standalone OWA.
            themesAllowed = true;
            let useV21TeamsTheme: boolean = false;
            // Only call getConfigSetting for the Calendar workload where it should be supported.
            if (workload === 2) {
                useV21TeamsTheme =
                    await lazyGetConfigSetting.importAndExecute("enableTeamsV21Theme");
            }
            // Setting a new `themeStorageId` here so that the palette is not picked up from cache.
            themeStorageId = generateTeamsThemeId(teamsTheme, useV21TeamsTheme);
            
            // Do not initiate a theme change if the cached theme is identical to the teams theme.
            if (cachedThemeStorageId === themeStorageId) {
                return;
            }
            
            palette = await lazyGetTeamsPalette.importAndExecute(teamsTheme, useV21TeamsTheme);
            brandVariants = await lazyGetTeamsBrandVariants.importAndExecute(useV21TeamsTheme);
            
            if (teamsTheme === "contrast") {
                isDarkTheme = true;
                setIsTeamsHighContrast(true);
            }
            else {
                setIsTeamsHighContrast(false);
            }
        }
        
        // In certain MetaOS hubs we want to disable the accent colors (of buttons/links/etc) that the user might have set,
        // to better match the hub's theme which doesn't support them (e.g. in Outlook win32), so we use the base theme.
        else if (isHostAppFeatureEnabled("useBaseTheme")) {
            themeStorageId = "base";
        }
        if (isInitializeThemeRunningFirstTime) {
            lazyHandleThemeChange.importAndExecute(initializeTheme);
            if (isGlobalFeatureEnabled("cal-init-registerInitializeThemeWithOnLoadHandler")) {
                // The callback function onTabLoaded passed in the registerOnLoadHandler will be called when the meta OS app is loaded back from the cache.
                // Please refer https://learn.microsoft.com/en-us/microsoftteams/platform/tabs/how-to/app-caching#enable-app-caching for more details.
                lazyRegisterOnLoadHandler.importAndExecute({
                    key: "initializeTheme",
                    handler: initializeTheme,
                    priority: getOwaWorkload() === 2 &&
                        isBootFeatureEnabled("cal-appCaching-nonBlockingTheme")
                        ? "nonBlocker" : "blocker",
                });
            }
        }
    }
    else {
        // The loadThemeFromHostApp and platformAppSdk host app features
        // are not intended to execute in Monarch, (see flagsDefaults.ts). Additionally, this code needs to be
        // in the else chain, otherwise we would be overriding the dark mode for opx and hub apps
        const primeSettings = getUserConfiguration()?.PrimeSettings;
        
        useSystemDarkModeSettings = !!primeSettings?.Items?.filter(item => item.Id === "UseSystemDarkMode").map(item => item.Value?.options?.[0].useSystemDarkMode)[0];
        
        if (useSystemDarkModeSettings) {
            isDarkTheme = getIsSystemDarkModePreferred();
        }
    }
    
    if (isHostAppFeatureEnabled("showOfficeNeutralTheme") &&
        themeStorageId == ThemeConstants.BASE_OFFICE_THEME_ID) {
        // Use the office neutral theme in the primary Mail scenario now. Leave other scenarios like OPX to use the default blue Office theme's for their own logic.
        const primeSettings = getAccountScopeUserSettings(getGlobalSettingsAccountMailboxInfo())?.PrimeSettings;
        let selectofficeNeutralThemeBit = false;
        
        if (primeSettings?.Items) {
            for (const primeSetting of primeSettings.Items) {
                if (primeSetting.Id === "OfficeNeutralThemeOptions") {
                    const officeNeutralThemeBitOption: any = primeSetting.Value?.options?.[0];
                    selectofficeNeutralThemeBit =
                        officeNeutralThemeBitOption?.isSelected as boolean;
                    break;
                }
            }
        }
        
        if (selectofficeNeutralThemeBit) {
            themeStorageId = ThemeConstants.OFFICE_NEUTRAL_THEME_ID;
        }
        else if (isCapabilitySupported(copilotThemeCapability)) {
            const copilotThemeSettingsJSON = primeSettings?.Items?.filter(item => item.Id === "CopilotThemeOptions").map(item => item.Value?.[0]?.value)[0];
            
            if (copilotThemeSettingsJSON) {
                const copilotThemeOptions: CopilotThemeOptions = JSON.parse(copilotThemeSettingsJSON);
                
                const isCopilotThemeSelected = !!copilotThemeOptions?.isSelected;
                
                if (isCopilotThemeSelected) {
                    themeStorageId = copilotThemeOptions.themeId;
                    
                    const copiloThemeResource = isDarkTheme
                        ? copilotThemeOptions.darkThemeResources
                        : copilotThemeOptions.lightThemeResources;
                    
                    palette = copiloThemeResource?.themePalette;
                    brandVariants = copiloThemeResource?.brandVariants;
                }
            }
        }
    }
    
    cachedThemeStorageId = themeStorageId;
    
    // TODO: Remove this temporary telemetry once AB#417970 fix is validated (AB#418211)
    logStartUsage("InitializeThemeResolved", tryGetGlobalSettingsAccountMailboxInfo(), {
        isDarkTheme,
        themesAllowed,
        themeStorageId,
        isFirstRun: isInitializeThemeRunningFirstTime,
        primaryColor: palette?.themePrimary,
        hasBrandVariants: !!brandVariants,
        useSystemDarkModeSettings,
    });
    
    await loadInitialTheme(themeStorageId, isDarkTheme, themesAllowed, useSystemDarkModeSettings, palette, brandVariants);
    
    if (isInitializeThemeRunningFirstTime) {
        initializeIsMsHighContrastState();
    }
}, "ti");
export default defaultExport;
